import { type JSX, useEffect, useRef, useState } from 'react';
import { UNRELIABLE_DAMAGE_FLAGS } from '../../../src/wire';
import type { ChronicleEntry } from '../chronicle';
import { formatDayMonth, formatEuros } from '../format';
import {
  CHRONICLE,
  flagLabel,
  navCategoryLabel,
  ROAD_ROLE_LABELS,
  SOURCE_CHIP_LABELS,
  SOURCE_LABELS,
} from '../labels';
import { DATE_NOTE_TITLE, dateNoteFor, flagChip, place } from './IncidentCard';

/**
 * One chronicle row: radio-log meta line, a source-coloured chip, the paragraph, the press release.
 *
 * A compact re-skin of the two incident cards and their rules, not a new reading of the data: the
 * digest keeps its category and its euros, and the traffic log keeps its roles and never shows money.
 */

/** The dot repeats what the chip already says in words: the series. Colour is the redundant half —
 * this is the site's one two-source list, and provenance may never be hover-only or colour-only. */
const DOT: Record<ChronicleEntry['source'], string> = {
  digest: 'var(--chart-1)',
  traffic: 'var(--chart-2)',
};

/** Widened exactly as `TrafficIncidentCard` widens it: an unlabelled role is printed as it came. */
const ROLES: Readonly<Record<string, string>> = ROAD_ROLE_LABELS;

/** The chip's words: the digest's category, or the log's distinct roles in the paragraph's order. */
function chipLabel(entry: ChronicleEntry): string {
  if (entry.source === 'digest') return navCategoryLabel(entry.item.category);
  const roles = [...new Set(entry.item.road_roles)].map((r) => ROLES[r] ?? r);
  return roles.length === 0 ? SOURCE_LABELS.traffic : roles.join(' · ');
}

/** `showDay` when the extension widened the window past its named day: a row from the day before
 * must not read as one from the day in the header. */
export function ChronicleItem({
  entry,
  showDay = false,
}: {
  entry: ChronicleEntry;
  showDay?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  // Measured on every resize, not guessed once: a "veel ›" that expands nothing is an affordance
  // that lies, and this row is 21rem wide in the rail and a phone wide in the Sheet.
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const node = textRef.current;
    if (node === null) return;
    const measure = (): void => setOverflows(node.scrollHeight > node.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const { item, source } = entry;
  const where = place(item.city, item.location_detail);
  // The date-only fallback: a paragraph that never stated a clock gets its day instead of a blank.
  const day = formatDayMonth(item.effective_date);
  const stamp =
    item.occurred_time === null
      ? day
      : showDay
        ? `${day} ${item.occurred_time}`
        : item.occurred_time;
  // And the card's own mark for a date the text never stated — on this corpus, most rows.
  const dateNote = dateNoteFor(item.occurred_precision);
  // No room for `excludedDamageNote`'s sentence, so a doubted sum is not shown at all.
  const doubted = item.flags.some((flag) => UNRELIABLE_DAMAGE_FLAGS.some((f) => f === flag));

  return (
    <li className="border-t border-line py-2.5">
      <p className="m-0 mb-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 text-caption text-ink-muted">
        <span className="font-semibold text-ink tabular-nums">
          {stamp}
          {where !== null && ` · ${where}`}
        </span>
        {dateNote !== null && (
          <span className="font-normal text-ink-muted" title={DATE_NOTE_TITLE}>
            {dateNote}
          </span>
        )}
        {/* No `title`: it would become the accessible name and mask the words below it. The full
            series name is in the panel's provenance line. */}
        <span className="inline-flex items-center gap-1.5 font-semibold text-ink-secondary">
          <span
            aria-hidden="true"
            className="size-2 shrink-0 rounded-full ring-1 ring-line-strong"
            style={{ background: DOT[source] }}
          />
          {SOURCE_CHIP_LABELS[source]} · {chipLabel(entry)}
        </span>
        {source === 'digest' && item.damage_cents !== null && !doubted && (
          <span className="font-semibold text-ink-secondary tabular-nums">
            {formatEuros(item.damage_cents)}
          </span>
        )}
        {flagChip(item.flags, flagLabel)}
      </p>

      <p ref={textRef} className={`m-0 mb-1 text-body leading-snug ${open ? '' : 'line-clamp-3'}`}>
        {item.text}
      </p>

      <p className="m-0 flex flex-wrap gap-x-3 text-caption">
        {(overflows || open) && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="font-semibold text-accent hover:text-accent-deep"
          >
            {open ? CHRONICLE.collapse : CHRONICLE.expand}
          </button>
        )}
        <a
          href={item.post_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent hover:text-accent-deep"
        >
          {CHRONICLE.report}
        </a>
      </p>
    </li>
  );
}
